import CloseIcon from '@mui/icons-material/Close';
import GroupIcon from '@mui/icons-material/GroupOutlined';
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton } from '@mui/material';
import { SyntheticEvent, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Carousel } from '../../components/carousel/Carousel.component';
import Spinner from '../../components/spinner/Spinner.component';
import { GameContext } from '../../core/context/game.context';
import { pushGtmDataLayer } from '../../core/helper/gtm.helper';
import { safeImageImport } from '../../core/helper/image.helper';
import { randomHelper } from '../../core/helper/random.helper';
import { PlaySummary } from '../../core/interfaces/challenge.interface';
import { IInstanceInfo, IPreset, PresetId } from '../../core/interfaces/instaces.interfaces';
import { ChlDialog } from './Dialog/Dialog.component';
import { EmailReq } from './summary.interface';
import './summary.style.scss';

export default function Summary({
  score,
  summary,
  onSend,
}: {
  score: number;
  summary?: PlaySummary;
  onSend: (arg: EmailReq) => Promise<undefined>;
}) {
  const [emailState, setEmailState] = useState<EmailReq>();
  const [emailModalOpenState, setEmailModalOpenState] = useState(false);
  const [dialogOpenState, setDialogOpenState] = useState(false);
  const [taglineState, setTaglineState] = useState<string>();
  const [emailSendState, setEmailSendState] = useState<boolean>();
  const gameCtx = useContext(GameContext);
  const vocale = score === gameCtx.getPreset()?.stepScore ? 'a' : 'e';
  const chosenPresetIdx = (() => gameCtx.getInstance().presets.findIndex((p) => p.id === gameCtx.getPreset().id))();
  const [styleState, setStyleState] = useState({ section: null });
  const mediaQuery = window.matchMedia('(max-width: 767.98px)');
  const { t } = useTranslation();
  const theme = gameCtx.getTheme();
  const instanceList = useRef(mapGameInstances());
  const navigate = useNavigate();

  useEffect(() => {
    if (gameCtx.getInstance()) {
      const manageBg = (arg: MediaQueryListEvent, rndValue: number) => {
        if (!gameCtx.getInstance()) {
          return;
        }
        const size = arg.matches ? 'small' : 'big';
        const instance = gameCtx.getInstance();

        if (instance.name === 'autogrill' || instance.challenge?.name?.includes('autogrill')) {
          setDialogOpenState(true);
        }

        setStyleState((state) => ({
          ...state,
          section: {
            backgroundImage: `url(${safeImageImport(`assets/images/${gameCtx.values.instance.id}/bg/${size}/0${rndValue}.webp`)}), url(${safeImageImport(`assets/images/${gameCtx.values.instance.id}/bg/${size}/0${rndValue}`)})`,
          },
        }));
      };

      const rndValue = randomHelper(gameCtx.getInstance().configurations.bgQuantity);
      const changeHandler = (e: MediaQueryListEvent) => manageBg(e, rndValue);

      manageBg({ matches: mediaQuery.matches } as MediaQueryListEvent, rndValue);
      mediaQuery.addEventListener('change', changeHandler);
      return () => mediaQuery.removeEventListener('change', changeHandler);
    }
  }, [gameCtx.getInstance()]);

  useEffect(() => {
    const taglines = gameCtx.getInstance().labelsSet.summary.taglines as unknown as string[];
    const textIds = Math.round(
      (((taglines || []).length - 1) * score) / (gameCtx.getPreset().maxQuestions * gameCtx.getPreset().stepScore)
    );

    setTaglineState(summary?.text || taglines?.[textIds]);
  }, [gameCtx, score, summary]);

  function mapGameInstances() {
    const actualInstance = gameCtx.getInstance().id;
    const actualPreset = gameCtx.getPreset().id;
    const instances = gameCtx.getAllInstances();
    let instancesPresets: { preset: IPreset; instance: IInstanceInfo }[] = [];

    instances.forEach((instance) => {
      const a = instance.presets.map((preset) => ({ preset, instance }));
      instancesPresets = instancesPresets.concat(a);
    });

    return instancesPresets
      .filter((ip) => {
        if (ip.instance.id === actualInstance && ip.preset.id === actualPreset) {
          return false;
        }
        return true;
      })
      .sort(() => Math.random() - 0.5)
      .map((prInst) => {
        const image =
          prInst.preset.cardImageUrl ||
          safeImageImport(`pages/summary/assets/challenge/${prInst.instance.name}-${prInst.preset.name}`);

        return {
          presetId: prInst.preset.id,
          instance: prInst.instance,
          link: `/${gameCtx.getLanguage()}/${prInst.instance.id}/${prInst.instance.name}/${prInst.preset.id}/${prInst.preset.name}/game`,
          picture: {
            image,
            title: prInst.instance.name,
          },
        };
      });
  }

  function playAgain() {
    pushGtmDataLayer('playAgain');
    gameCtx.deleteGame();
    navigate(
      `/${gameCtx.getLanguage()}/${gameCtx.getInstance().id}/${gameCtx.getInstance().name}/${gameCtx.getPreset().id}/${gameCtx.getPreset().name}/game`
    );
  }

  async function sendEmail(e: SyntheticEvent) {
    e.preventDefault();
    if (emailSendState !== undefined) {
      handleClose();
      return;
    }
    try {
      await onSend({
        name: emailState.name,
        recipientEmail: emailState.recipientEmail,
        emailTplId: gameCtx.getInstance().emailTplId,
      });
      setEmailSendState(true);
    } catch (e) {
      setEmailSendState(false);
    }
    pushGtmDataLayer('friendChallenge');
  }

  // patch for gameCtx update delay
  function slideClickHandler(e: SyntheticEvent, elem: { presetId: PresetId; instance: IInstanceInfo }) {
    gameCtx.setInstance(elem.instance);
    gameCtx.setPresetId(elem.presetId);
  }

  function updateEmailState(field: string, value: string | number) {
    setEmailState((state) => ({ ...state, [field]: value }));
  }

  function handleOpen() {
    setEmailModalOpenState(true);
  }

  function handleClose() {
    setEmailModalOpenState(false);
  }

  function sendEvent(evt: 'googlePlayCTA' | 'appStoreCTA') {
    pushGtmDataLayer(evt);
  }

  return (
    <section className="SummaryComponent" style={styleState.section}>
      {!gameCtx.getInstance() ? (
        <Spinner primaryColor="#7DDC55" secondaryColor="#FE6E5C" />
      ) : (
        <>
          <div className="container">
            <div className="container__card">
              <img
                className="container__logo"
                src={
                  gameCtx.getPreset()?.logo ||
                  safeImageImport(`assets/images/${gameCtx.getInstance().id}/loghi/logo-inline`)
                }
                alt="loquis-challenge-logo"
              />
              <div className="container__info">
                <div
                  className={
                    'difficulty difficulty--' +
                    chosenPresetIdx +
                    (!gameCtx.getPreset().name ? ' difficulty--noLabel' : '')
                  }
                >
                  <p className="container__card__title" style={{ color: theme.main[0] }}>
                    {`${score / gameCtx.getPreset().stepScore} ${t('summary.showdown').replaceAll('§vocal§', vocale)} ${
                      gameCtx.getPreset().maxQuestions
                    }`}
                  </p>
                  <p className="container__card__tagline">{taglineState}</p>
                </div>
                <div className="share">
                  <button
                    className={'basicButton' + (theme.main[2] ? '' : ' whiteButton')}
                    style={{
                      marginBottom: '4%',
                      backgroundColor: theme.main[2],
                      color: theme.main[0],
                    }}
                    onClick={handleOpen}
                  >
                    {t('summary.chl-friend')}
                  </button>
                </div>
              </div>
              {!!instanceList.current.length && (
                <Carousel
                  list={instanceList.current}
                  title={t('summary.keep-playing')}
                  onSlideClick={slideClickHandler}
                />
              )}
              <div className="container__card__cta">
                <p style={{ marginBottom: '2%' }}>{t('summary.download')} </p>
                <a
                  href="https://itunes.apple.com/it/app/loquis-esplora-e-ascolta-roma/id1358486157?mt=8"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => sendEvent('appStoreCTA')}
                  style={{ marginRight: '3%' }}
                >
                  <img
                    className="container__card__cta__store"
                    alt="download app store"
                    src={safeImageImport('assets/images/badge-app-store')}
                    width="114"
                  />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=it.georadio.loquis"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => sendEvent('googlePlayCTA')}
                >
                  <img
                    className="container__card__cta__store"
                    alt="download play store"
                    src={safeImageImport('assets/images/badge-play-store')}
                    width="114"
                  />
                </a>
              </div>
            </div>
            {!instanceList.current.length && (
              <button
                className="basicButton"
                style={{ marginTop: '8vh', backgroundColor: theme.main[2], color: theme.main[0] }}
                onClick={playAgain}
              >
                {t('summary.play-more')}
              </button>
            )}
          </div>
          <Dialog open={emailModalOpenState} onClose={handleClose}>
            <form className="dialogContainer" onSubmit={sendEmail}>
              <DialogContent>
                <GroupIcon className="dialogIcon" />
                <DialogTitle className="dialogTitle">
                  {t('summary.chl-friend')}
                  <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                      position: 'absolute',
                      right: 8,
                      top: 8,
                      color: (theme) => theme.palette.grey[500],
                    }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                {(() => {
                  switch (emailSendState) {
                    case undefined:
                      return (
                        <>
                          <DialogContentText className="dialogText">{t('summary.challenge')}</DialogContentText>
                          <input
                            className="textfield"
                            autoFocus
                            id="name"
                            placeholder={t('summary.submit-name')}
                            type="text"
                            onChange={(e) => updateEmailState('name', e.target.value)}
                            required
                          />
                          <input
                            className="textfield"
                            autoFocus
                            id="email"
                            placeholder={t('summary.submit-email')}
                            type="email"
                            onChange={(e) => updateEmailState('recipientEmail', e.target.value)}
                            required
                          />
                        </>
                      );
                    case true:
                      return <DialogContentText className="dialogText">{t('summary.email-sent')}</DialogContentText>;
                    case false:
                      return <DialogContentText className="dialogText">{t('summary.email-error')}</DialogContentText>;
                  }
                })()}
              </DialogContent>
              <DialogActions className="dialogAction">
                <button className="basicButton basicButton--hover" type="submit">
                  {emailSendState === undefined ? t('summary.send-invitation') : t('summary.close')}
                </button>
              </DialogActions>
            </form>
          </Dialog>
          <ChlDialog open={dialogOpenState} />
        </>
      )}
    </section>
  );
}
